/**
 * §12.2's RBAC matrix, mirrored on the client for ONE purpose: deciding what to RENDER.
 *
 * The server is the authority and this table is not — `src/bayram/admin/security/permissions.py`
 * holds the real matrix and every route calls `require(...)`. What this buys is role-based
 * **hiding**, not disabling. A privileged button an operator cannot use is worse than no
 * button: pressing it writes a `permission.denied` audit row against someone who did nothing
 * wrong, and it teaches them that half the console is broken.
 *
 * Drift is safe in one direction only. Stricter than the server costs an operator an
 * affordance; looser hands them a 403 and that audit row. So when in doubt, omit a role.
 *
 * Only the cells this app can actually spend are listed. A screen that needs another one adds
 * it here rather than testing `role === "owner"` inline — three inline role checks disagree
 * eventually, and the disagreement is always a grant.
 *
 * The wire spells the third role `"admin"`; §12.2's table heads that column OPERATOR. Same
 * role. The wire spelling wins, because `/auth/me` renders `"admin"`.
 */

import type { MeResponse } from "@/api/auth";
import { useAuthStore } from "@/state/auth";

/** The four roles, as `/auth/me` spells them. Taken from the API type, never restated. */
export type AdminRole = MeResponse["role"];

/**
 * The cells this console renders against.
 *
 * `reveal.personal_data` is the `A+S` cell `POST /api/reveal` ultimately spends; the server
 * also guards the router with its ROLE half (`reveal.personal_data.read`, SUPPORT and above)
 * so that a correctly re-authenticated SUPPORT operator is not refused by a guard that holds
 * no subject. That split is a server mechanism and is deliberately not mirrored: both halves
 * name the same three roles here.
 *
 * `user.block.write` and `credit.grant.write` are the ROLE halves the two writers are guarded
 * by — `permissions.py` gives them `_row(admin=_W, owner=_W)`. They are what a Block or a
 * Grant button must be gated on, because they are what the routers actually check.
 *
 * `rail.control` and `payment.notify` are the Payme rail's two cells and they are deliberately
 * SEPARATE. Both are plain `W` on the server — neither is a step-up cell — but they answer
 * different questions: `rail.control` stops the business selling, and `payment.notify` re-sends
 * a confirmation a customer was already owed. Riding the notify on `rail.control` would have
 * been one fewer constant and would have handed a support operator the switch; giving pause a
 * step-up would have been worse than either, because `check_role` holds no subject and so
 * answers `STEP_UP_REQUIRED` for ever at OWNER while looking exactly correct.
 */
export type Permission =
  | "reveal.personal_data"
  | "user.block.write"
  | "credit.grant.write"
  | "broadcast.write"
  | "rail.control"
  | "payment.notify"
  | "support.write"
  | "support.group.write"
  | "config.manage";

const SUPPORT_UP: readonly AdminRole[] = ["support", "admin", "owner"];
const OPERATOR_UP: readonly AdminRole[] = ["admin", "owner"];
const OWNER_ONLY: readonly AdminRole[] = ["owner"];

/** Which roles hold a cell. A role absent from the list has none, which is a denial. */
export const RBAC_MATRIX: Readonly<Record<Permission, readonly AdminRole[]>> = {
  "reveal.personal_data": SUPPORT_UP,
  "user.block.write": OPERATOR_UP,
  "credit.grant.write": OPERATOR_UP,
  "broadcast.write": OPERATOR_UP,
  "rail.control": OPERATOR_UP,
  "payment.notify": SUPPORT_UP,
  "support.write": SUPPORT_UP,
  "support.group.write": OPERATOR_UP,
  "config.manage": OWNER_ONLY,
};

/**
 * Whether a role holds a permission.
 *
 * An unknown role — `null` before `bootstrap()` answers, or after a session ends — holds
 * nothing, so a privileged control never flashes on screen for someone who cannot use it.
 */
export function hasPermission(role: AdminRole | null, permission: Permission): boolean {
  return role !== null && RBAC_MATRIX[permission].includes(role);
}

/** VIEWER has no reveal cell at all: the affordance is absent, not disabled (rule 4). */
export function canReveal(role: AdminRole | null): boolean {
  return hasPermission(role, "reveal.personal_data");
}

/** Block and unblock share one cell: they are the two directions of one idempotent write. */
export function canBlockUsers(role: AdminRole | null): boolean {
  return hasPermission(role, "user.block.write");
}

export function canGrantCredits(role: AdminRole | null): boolean {
  return hasPermission(role, "credit.grant.write");
}

/**
 * Whether this role may COMPOSE a campaign — create it, revise it, send it.
 *
 * The server's `broadcast.write` cell is ADMIN and OWNER; reading the campaign list and one
 * campaign's detail is `broadcast.read`, which every role holds, and neither is mirrored here
 * because neither hides anything. This one does: "Message these users" on the directory is a
 * button that would 403 for a viewer or a support operator, and a 403 on a press is a
 * `permission.denied` audit row against somebody who did nothing wrong.
 *
 * The step-up half (`broadcast.send`) is deliberately NOT a separate cell here. It is held by
 * the same two roles and is spent through a password round trip the dialog already drives, so
 * a second constant would only be a second thing to keep in step.
 */
export function canWriteBroadcasts(role: AdminRole | null): boolean {
  return hasPermission(role, "broadcast.write");
}

/**
 * Whether this role may PAUSE or RESUME the checkout rail.
 *
 * ADMIN and OWNER. Used to hide the switch, never to disable it: a support operator who
 * pressed it would get a 403 and a `permission.denied` audit row against somebody who did
 * nothing wrong, and would learn that a section of the console is broken.
 *
 * There is no step-up half to mirror. `permissions.py` keeps `RAIL_CONTROL` out of
 * `STEP_UP_ACTIONS` on purpose, and if that is ever revisited the remedy on the server is a
 * `RAIL_CONTROL_WRITE` + `RAIL_CONTROL` split — never adding the existing member to the
 * mapping, which would ship a route that refuses an owner for ever. Nothing changes here
 * either way: this constant names the ROLE half, which is what the router checks.
 */
export function canControlRail(role: AdminRole | null): boolean {
  return hasPermission(role, "rail.control");
}

/**
 * Whether this role may re-send a payment confirmation.
 *
 * SUPPORT and above, which is a wider cell than `rail.control` on purpose: support is who
 * takes the "I paid and nothing happened" call, and this is the single most common answer to
 * it. It writes no money row and no state a customer can spend.
 */
export function canNotifyPayment(role: AdminRole | null): boolean {
  return hasPermission(role, "payment.notify");
}

/**
 * Whether this role may MOVE a ticket, claim it, note on it or answer the customer.
 *
 * SUPPORT and above — **the only write cell in this table that starts at SUPPORT rather than at
 * ADMIN**, and the asymmetry is the point rather than an oversight. A support operator's entire
 * job is answering customers; withholding the reply would leave the role unable to do the thing
 * it is named for and would push the work back into the Telegram support group, where a staffer
 * is identified by chat membership alone and nothing writes an audit row at all. The server's
 * `SUPPORT_WRITE` argues it at length and is the authority here.
 *
 * **There is no step-up half to mirror, on any of the four writes.** `permissions.py` keeps
 * `SUPPORT_WRITE` out of `STEP_UP_ACTIONS` deliberately, and both support cells are therefore
 * safe as router-level guards — which is the trap that has been sprung five times in this
 * package: `require_permission` → `check_role` holds no subject and so answers
 * `STEP_UP_REQUIRED` for ever, to a correctly re-authenticated OWNER included, while looking
 * exactly correct.
 *
 * Reading the board is `support.read`, `M` at every role, and is deliberately NOT mirrored here:
 * it hides nothing. This one hides the drag handles, the keyboard pick-up and every move
 * control — a VIEWER who dragged a card would get a 403 and a `permission.denied` audit row
 * against somebody who did nothing wrong, and would learn that the board is broken.
 */
export function canWriteSupport(role: AdminRole | null): boolean {
  return hasPermission(role, "support.write");
}

/**
 * Whether this role may REPOINT the support inbox — choose which Telegram group and topic every
 * future ticket card is posted into, or stop posting altogether.
 *
 * **ADMIN and OWNER, and deliberately NOT the cell beside it.** This is the one place in the
 * table where two permissions over one screen's worth of subject matter take different rows, so
 * the reason is worth stating rather than inferring from the server: `support.write` is a
 * support operator doing their job, one customer at a time, and every act of it is visible on a
 * timeline the next operator reads. Repointing the inbox is configuration of the desk itself,
 * done once and then not again for months, and getting it wrong publishes every future
 * complaint into whatever room was chosen — including a private channel nobody on the team
 * reads, which looks identical from this console until somebody notices the tickets stopped
 * arriving. A support operator works the queue; they do not decide where the queue lands.
 *
 * **There is no step-up half.** `SUPPORT_GROUP_WRITE` is a plain `W` — `permissions.py` argues
 * it, transcribing `broadcast.write`'s trade: the audit row plus the standing
 * `selected_by_username`/`selected_at` on the chosen row carry the accountability, and the act
 * is undone by selecting the previous group again.
 *
 * Reading which group is selected is `support.read`, `M` at every role and deliberately NOT
 * mirrored here: it hides nothing, and "where do my tickets go?" is a question every operator
 * must be able to answer without being able to change the answer. This constant hides the
 * Select buttons, the paste field and the Clear control; a VIEWER who pressed one would get a
 * 403 and a `permission.denied` audit row against somebody who did nothing wrong.
 */
export function canWriteSupportGroup(role: AdminRole | null): boolean {
  return hasPermission(role, "support.group.write");
}

/** The signed-in role, or `null` while unknown. The one place a component reads it. */
export function useRole(): AdminRole | null {
  return useAuthStore((state) => state.account?.role ?? null);
}

export function useCanReveal(): boolean {
  return canReveal(useRole());
}

export function useCanBlockUsers(): boolean {
  return canBlockUsers(useRole());
}

export function useCanGrantCredits(): boolean {
  return canGrantCredits(useRole());
}

export function useCanWriteBroadcasts(): boolean {
  return canWriteBroadcasts(useRole());
}

export function useCanControlRail(): boolean {
  return canControlRail(useRole());
}

export function useCanNotifyPayment(): boolean {
  return canNotifyPayment(useRole());
}

export function useCanWriteSupport(): boolean {
  return canWriteSupport(useRole());
}

export function useCanWriteSupportGroup(): boolean {
  return canWriteSupportGroup(useRole());
}

/**
 * Whether this role may change runtime configuration (e.g. music provider).
 *
 * OWNER only. Router guard on POST /api/config/music-provider enforces Permission.CONFIG_MANAGE.
 */
export function canManageConfig(role: AdminRole | null): boolean {
  return hasPermission(role, "config.manage");
}

export function useCanManageConfig(): boolean {
  return canManageConfig(useRole());
}

